import {
  DateTimePickerAndroid,
  type DateTimePickerChangeEvent,
} from "@react-native-community/datetimepicker";
import {
  createElement,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import { IOSPickerSheet } from "@/components/ui/ios-picker-sheet";

type DateOfBirthPickerProps = {
  value: string;
  onChange: (value: string) => void;
  minimumDate: Date;
  maximumDate: Date;
  placeholder?: string;
  leading?: ReactNode;
  rootStyle?: StyleProp<ViewStyle>;
  inputContainerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  placeholderTextColor?: string;
};

function parseDateInput(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateInput(date: Date): string {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function DateOfBirthPicker({
  value,
  onChange,
  minimumDate,
  maximumDate,
  placeholder,
  leading,
  rootStyle,
  inputContainerStyle,
  textStyle,
  placeholderTextColor = "#99A4B4",
}: DateOfBirthPickerProps) {
  const { t } = useTranslation();
  const [showIOSPicker, setShowIOSPicker] = useState(false);

  const selectedDate = useMemo(
    () => parseDateInput(value) ?? maximumDate,
    [maximumDate, value],
  );

  const resolvedPlaceholder =
    placeholder ?? t("profile.a11y.selectDateOfBirth");
  const displayValue = value || resolvedPlaceholder;
  const displayStyle = [
    styles.displayText,
    textStyle,
    !value ? { color: placeholderTextColor } : null,
  ];

  function commitDate(date: Date) {
    onChange(formatDateInput(date));
  }

  function onNativeValueChange(
    _event: DateTimePickerChangeEvent,
    nextDate: Date,
  ) {
    commitDate(nextDate);
  }

  function openPicker() {
    if (Platform.OS === "android") {
      DateTimePickerAndroid.open({
        value: selectedDate,
        mode: "date",
        display: "default",
        minimumDate,
        maximumDate,
        onValueChange: onNativeValueChange,
        onDismiss: () => {},
      });
      return;
    }

    setShowIOSPicker(true);
  }

  if (Platform.OS === "web") {
    return (
      <View
        style={[
          styles.root,
          rootStyle,
          styles.inputContainer,
          inputContainerStyle,
        ]}
      >
        {leading}
        {createElement("input", {
          type: "date",
          value,
          min: formatDateInput(minimumDate),
          max: formatDateInput(maximumDate),
          "aria-label": resolvedPlaceholder,
          onClick: (event: ReactMouseEvent<HTMLInputElement>) => {
            event.currentTarget.showPicker?.();
          },
          onChange: (event) => {
            onChange((event.currentTarget as HTMLInputElement).value);
          },
          style: StyleSheet.flatten([
            styles.webInput,
            textStyle,
            !value ? { color: placeholderTextColor } : null,
          ]),
        })}
      </View>
    );
  }

  return (
    <View style={[styles.root, rootStyle]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("profile.a11y.chooseDateOfBirth")}
        onPress={openPicker}
        style={[styles.inputContainer, inputContainerStyle]}
      >
        {leading}
        <Text style={displayStyle}>{displayValue}</Text>
      </Pressable>

      {/* In a sheet, not inline. Inline it had no dismissal at all: tapping
          outside did nothing and the only way out was to find the field again
          and tap it a second time, which nothing on screen suggested. */}
      {Platform.OS === "ios" ? (
        <IOSPickerSheet
          visible={showIOSPicker}
          value={selectedDate}
          onValueChange={onNativeValueChange}
          onClose={() => setShowIOSPicker(false)}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          accessibilityLabel={t("profile.a11y.chooseDateOfBirth")}
        />
      ) : null}
    </View>
  );
}

export function maximumDateForMinimumAge(minimumAge: number): Date {
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear() - minimumAge,
      now.getUTCMonth(),
      now.getUTCDate(),
    ),
  );
}

export function minimumDateForMaximumAge(maximumAge: number): Date {
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear() - maximumAge,
      now.getUTCMonth(),
      now.getUTCDate(),
    ),
  );
}

const styles = StyleSheet.create({
  root: {
    alignSelf: "stretch",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  displayText: {
    flex: 1,
  },
  webInput: {
    flex: 1,
    borderWidth: 0,
    backgroundColor: "transparent",
  },
});
